import { Link } from "react-router-dom";
import {
  Bookmark,
  BookOpen,
  Brain,
  Calculator,
  Radar,
  Settings as SettingsIcon,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Panel } from "@/components/common/Primitives";

const STEPS = [
  {
    n: 1,
    title: "Scannen",
    text: "Kies een merk/model (bijv. Porsche 911) en filters zoals bouwjaar, kilometerstand en prijs. ImDash haalt echte advertenties op van AutoScout24.",
  },
  {
    n: 2,
    title: "Rekenen",
    text: "Voor elke auto berekent het systeem BPM, importkosten, de verwachte NL-verkoopwaarde en de winst — altijd op basis van échte data, nooit verzonnen.",
  },
  {
    n: 3,
    title: "Rangschikken",
    text: "Elke auto krijgt een Deal Score van 0 tot 100. De beste handelskansen staan automatisch bovenaan.",
  },
  {
    n: 4,
    title: "Beslissen",
    text: "Open de detailpagina, bekijk de volledige onderbouwing en zet interessante auto's met één klik op je watchlist.",
  },
];

const FEATURES = [
  {
    icon: Radar,
    title: "Zoeken & scannen",
    to: "/nieuwe-scan",
    linkLabel: "Naar Nieuwe Scan",
    items: [
      "Live zoeken op AutoScout24 (Duitsland/Nederland)",
      "Zoekprofielen opslaan om met één klik opnieuw te scannen",
      "Overzicht van nieuwe & gewijzigde advertenties, inclusief prijsverlagingen",
    ],
  },
  {
    icon: Calculator,
    title: "Automatische berekeningen",
    items: [
      "BPM op basis van officiële tarieven (1993–2026) en CO₂",
      "Importkosten: transport, RDW/administratie, keuring, garantie, verkoopklaar maken, onvoorzien",
      "Verwachte NL-waarde uit vergelijkbare auto's — in 3 scenario's (conservatief / basis / optimistisch)",
      "Handelsbijdrage, ROI én de maximale inkoopprijs om je winstdoel te halen",
    ],
  },
  {
    icon: Target,
    title: "Deal Score & risico's",
    to: "/handelskansen",
    linkLabel: "Naar Handelskansen",
    items: [
      "Eén score (0–100) per auto met een duidelijk advies",
      "Risico-indicatoren uit de advertentietekst — altijd mét het letterlijke bewijs",
      "AI-toelichting in het Nederlands die de cijfers uitlegt (verzint nooit bedragen)",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Controle & transparantie",
    items: [
      "Elke waarde is herleidbaar naar een bron — CO₂ wordt nooit geraden",
      "Handmatig BPM, CO₂ of de NL-waarde aanpassen, met vermelding \u201Chandmatig\u201D",
      "Prijsgeschiedenis en een volledig auditspoor per auto",
    ],
  },
  {
    icon: Bookmark,
    title: "Beheer & instellingen",
    to: "/instellingen",
    linkLabel: "Naar Instellingen",
    items: [
      "Watchlist met doel-inkoopprijs per auto",
      "Instelbare winstdrempels, kostenaannames, waarderings- en BPM-regels, risico's en AI",
      "Integratiestatus + handmatige/JSON-invoer van eigen advertentiedata",
    ],
  },
  {
    icon: Users,
    title: "Voor meerdere gebruikers",
    items: [
      "Aparte accounts met strikte scheiding van data tussen klanten",
      "Master-beheerder en klant-beheerders",
      "Abonnementen via Stripe en maandelijkse gebruikslimieten (scans, AI-analyses, verversingen)",
    ],
  },
];

const StepCard = ({ n, title, text }) => (
  <div className="panel p-3" data-testid={`help-step-${n}`}>
    <div className="flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 font-display text-[12px] font-bold text-white">
        {n}
      </span>
      <h3 className="font-display text-[13px] font-semibold text-slate-900">{title}</h3>
    </div>
    <p className="mt-2 text-[12px] leading-snug text-slate-600">{text}</p>
  </div>
);

const FeatureCard = ({ icon: Icon, title, items, to, linkLabel }) => (
  <Panel
    title={title}
    testid={`help-feature-${title}`}
    actions={<Icon className="h-4 w-4 text-blue-500" strokeWidth={2} />}
  >
    <ul className="space-y-1.5 p-3">
      {items.map((item) => (
        <li key={item} className="flex gap-2 text-[12.5px] leading-snug text-slate-700">
          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-blue-500" />
          {item}
        </li>
      ))}
    </ul>
    {to && (
      <div className="border-t border-slate-100 px-3 py-2">
        <Link to={to} className="text-[11.5px] font-semibold text-blue-700 hover:underline">
          {linkLabel} →
        </Link>
      </div>
    )}
  </Panel>
);

export default function Help() {
  return (
    <AppShell
      title="Help & Uitleg"
      subtitle="Wat ImDash doet en hoe je ermee werkt"
    >
      <div className="space-y-3" data-testid="help-page">
        <div className="panel overflow-hidden">
          <div className="flex items-start gap-3 bg-slate-900 px-4 py-4 text-white">
            <BookOpen className="mt-0.5 h-6 w-6 shrink-0 text-blue-400" strokeWidth={2} />
            <div>
              <h2 className="font-display text-[15px] font-bold tracking-tight">ImDash in het kort</h2>
              <p className="mt-1 max-w-3xl text-[12.5px] leading-snug text-slate-300">
                ImDash scant auto's in het buitenland, rekent automatisch alle Nederlandse
                importkosten en BPM uit, en zegt je direct of er handelswinst in zit — met de{" "}
                <span className="font-semibold text-white">Deal Score (0–100)</span>.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-2 font-display text-[13px] font-semibold tracking-tight text-slate-800">
            Zo werkt het in 4 stappen
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {STEPS.map((s) => (
              <StepCard key={s.n} {...s} />
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-2 flex items-center gap-2 font-display text-[13px] font-semibold tracking-tight text-slate-800">
            <SettingsIcon className="h-4 w-4 text-slate-400" />
            Belangrijkste functies
          </h2>
          <div className="grid gap-2 lg:grid-cols-2 xl:grid-cols-3">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>

        <div className="panel flex items-start gap-2.5 p-3">
          <Brain className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
          <p className="text-[11.5px] leading-snug text-slate-500">
            <span className="font-semibold text-slate-700">Belangrijk:</span> alle bedragen zijn
            indicaties op basis van opgeslagen marktdata en instelbare aannames. ImDash geeft
            beslissingsondersteuning — geen bindend aankoopadvies en geen boekhoudkundige nettowinst.
            De AI-laag interpreteert alleen de berekende cijfers en verzint nooit financiële waarden.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
